import React, { type FC } from "react"
import { Link } from "react-router-dom"
import type { EntityCategory } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgChevronRight } from "@digits-shared/components/SVGIcons/line/ChevronRight.svg"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { CATEGORY_COLOR_MAP } from "src/frontend/components/OS/Applications/Accounting/TransactionDashboard/TransactionUI"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import type { HierarchicalDimensionSummaryNode } from "src/shared/helpers/hierarchicalDimensionHelper"

/*
  STYLES
*/

const CrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CrumbLink = styled(Link)<{ color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  cursor: pointer;
  color: ${({ color }) => color};

  &.heavy {
    font-weight: ${fonts.weight.heavy};
  }

  &:hover {
    text-decoration: underline;
  }
`

const Chevron = styled(SvgChevronRight)`
  width: 15px;
  ${svgPathStyles(colors.secondary, 1.5)}
`

/*
  INTERFACES
*/

interface CrumbsProps {
  categorySummary: HierarchicalDimensionSummaryNode<EntityCategory>
}

/*
  COMPONENTS
*/

export const CategoryHierarchyCrumbs: FC<CrumbsProps> = ({ categorySummary }) => {
  const crumbs = useBreadCrumbs(categorySummary)
  return (
    <CrumbContainer>
      {crumbs.map((category, idx) => (
        <React.Fragment key={category.id}>
          <Crumb category={category} first={idx === 0} />
          {idx !== crumbs.length - 1 && <Chevron />}
        </React.Fragment>
      ))}
    </CrumbContainer>
  )
}

const Crumb: FC<{
  category: EntityCategory
  first: boolean
}> = ({ category, first }) => {
  const { id, name, type } = category
  const generatePath = useFrontendPathGenerator()
  const { currentLegalEntity } = useSession<FrontendSession>()
  const leSlug = currentLegalEntity?.slug

  const path = generatePath(
    routes.categoryDetails,
    { categoryId: id, leSlug },
    "includeAllQueryParams"
  )

  const color = first && CATEGORY_COLOR_MAP.get(type)
  return (
    <CrumbLink
      to={path}
      className={stringHelper.classNames(first && "heavy")}
      color={color || colors.secondary}
    >
      {name}
    </CrumbLink>
  )
}

function useBreadCrumbs(categorySummary: HierarchicalDimensionSummaryNode<EntityCategory>) {
  return React.useMemo(() => {
    const crumbs: EntityCategory[] = []
    let node = categorySummary.parent
    while (node) {
      crumbs.push(node.dimension)
      node = node.parent
    }
    return crumbs.reverse()
  }, [categorySummary.parent])
}
